import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  CircularProgress,
  ListItem,
  ListItemText,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  IconButton
} from '@mui/material';
import { FixedSizeList as List } from 'react-window';
import { API_BASE_URL } from 'src/apiConfig';
import { useParams, useNavigate } from 'react-router-dom';
import { cyrillicToLatin } from 'src/helper/helperFunction';
import { useTranslation } from 'react-i18next';
import AddStudentDialog from 'src/components/dialogs/student/addStudentDialog';
import StudentDetails from 'src/scenes/review/reviewLastStep - Check';
import { getToken, isAdmin, isSchool } from 'src/services/authService';
import CustomModal from 'src/components/dialogs/custom/CustomModal';
import api from 'src/services/api';
import { ActiveModesContext } from 'src/context/ActiveModesContext';
import {
  FLAG_FINALE,
  FLAG_REZULTATI,
  FLAG_ZALBE
} from 'src/constants/competitions';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { useTheme } from '@mui/material/styles';

const SchoolStudents = () => {
  const { schoolId } = useParams();
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openStudentDialog, setOpenStudentDialog] = useState(false);
  const { t } = useTranslation();
  const selectedStudentRef = useRef(null);
  const theme = useTheme();

  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();
  const [openComplaintExistsDialog, setOpenComplaintExistsDialog] =
    useState(false);

  // Proveri da li korisnik ima pravo pristupa
  useEffect(() => {
    if (!isAdmin() && !isSchool()) {
      setOpenDialog(true);
      return;
    }
  }, [t]);

  const handleDialogToggle = (status, data) => {
    setOpenStudentDialog(status);
    if (status) setSelectedStudent(data || null);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowDown') {
        const currentIndex = filteredStudents.findIndex(
          (student) => student.id === selectedStudentId
        );
        if (currentIndex < filteredStudents.length - 1) {
          const nextStudent = filteredStudents[currentIndex + 1];
          setSelectedStudentId(nextStudent.id);
          setSelectedStudent(nextStudent);
        }
      } else if (event.key === 'ArrowUp') {
        const currentIndex = filteredStudents.findIndex(
          (student) => student.id === selectedStudentId
        );
        if (currentIndex > 0) {
          const prevStudent = filteredStudents[currentIndex - 1];
          setSelectedStudentId(prevStudent.id);
          setSelectedStudent(prevStudent);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedStudentId, filteredStudents]);

  const fetchStudents = async () => {
    setLoading(true);
    const token = getToken();

    try {
      const response = await api.get(
        `${API_BASE_URL}/api/reviewAll.php?action=getStudentsBySchool&schoolId=${schoolId}`,
        {
          headers: {
            Authorization: `Bearer ${token.trim()}`
          }
        }
      );
      const fetchedStudents = response.data || [];

      setStudents(fetchedStudents);

      if (selectedStudentId) {
        const selected = fetchedStudents.find(
          (student) => student.id === selectedStudentId
        );
        if (selected) {
          setSelectedStudent(selected);
        }
      } else if (fetchedStudents.length > 0) {
        setSelectedStudentId(fetchedStudents[0].id);
        setSelectedStudent(fetchedStudents[0]);
      }
    } catch (error) {
      console.error('Greška prilikom preuzimanja učenika:', error);
      if (error.response && error.response.status === 403) {
        setOpenDialog(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [schoolId]);

  useEffect(() => {
    if (selectedStudentRef.current) {
      selectedStudentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [selectedStudentId]);

  useEffect(() => {
    const normalizedSearchTerm = cyrillicToLatin(searchTerm.toLowerCase());
    if (normalizedSearchTerm === '') {
      setFilteredStudents(students);
    } else {
      const filtered = students.filter((student) => {
        const normalizedStudentName = cyrillicToLatin(
          student.name.toLowerCase()
        );
        return normalizedStudentName.includes(normalizedSearchTerm);
      });
      setFilteredStudents(filtered);
    }
  }, [searchTerm, students]);

  const handleStudentClick = (student) => {
    setSelectedStudentId(student.id);
    setSelectedStudent(student);
  };

  const { activeModes } = useContext(ActiveModesContext);

  if (
    !isAdmin() &&
    !activeModes.includes(FLAG_REZULTATI) &&
    !activeModes.includes(FLAG_ZALBE) &&
    !activeModes.includes(FLAG_FINALE)
  ) {
    return (
      <Box
        display="grid"
        gap={3}
        p={2}
        sx={{
          backgroundColor: '#e0f7fa',
          borderRadius: '12px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Typography variant="h6" color="textSecondary" gutterBottom>
          {t('results.accessDenied')}
        </Typography>
      </Box>
    );
  }

  // Funkcija za rukovanje klikom na dugme "Nova žalba"
  const handleNewComplaint = async () => {
    if (!selectedStudent) {
      alert(t('Molimo Vas da prvo izaberete učenika.'));
      return;
    }

    try {
      const response = await api.get(
        `${API_BASE_URL}/api/complaints.php?schoolId=${schoolId}&grouped=true`
      );

      const complaints = response.data || [];

      const existingComplaint = complaints.find(
        (complaint) =>
          parseInt(complaint.id_ucenika, 10) ===
          parseInt(selectedStudent.id, 10)
      );

      if (existingComplaint) {
        // Žalba već postoji za ovog učenika
        setOpenComplaintExistsDialog(true);
      } else {
        // Žalba ne postoji, preusmeravamo na stranicu za kreiranje žalbe
        navigate('/createComplaint', {
          state: { studentId: selectedStudent.id }
        });
      }
    } catch (error) {
      console.error('Greška prilikom provere žalbe:', error);
      // Opcionalno, možete dodati rukovanje greškom
    }
  };

  return (
    <Grid container spacing={2} style={{ padding: '10px', height: '60vh' }}>
      <Grid item xs={3}>
        <Paper
          elevation={4}
          style={{
            padding: '20px',
            borderRadius: '16px',
            backgroundColor: '#f7f9fc',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            height: '400px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <TextField
            fullWidth
            label={t('review.searchStudents')}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: '20px' }}
          />
          <Box sx={{ flex: 1 }}>
            {loading ? (
              <CircularProgress />
            ) : (
              <List
                height={300}
                itemCount={filteredStudents.length}
                itemSize={50}
                width="100%"
              >
                {({ index, style }) => (
                  <ListItem
                    style={{
                      ...style,
                      backgroundColor:
                        filteredStudents[index].id === selectedStudentId
                          ? '#1976D2'
                          : '#f0f0f0',
                      color:
                        filteredStudents[index].id === selectedStudentId
                          ? '#fff'
                          : '#000',
                      borderRadius: '8px',
                      margin: '5px 0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                    button
                    key={filteredStudents[index].id}
                    onClick={() => handleStudentClick(filteredStudents[index])}
                    ref={
                      filteredStudents[index].id === selectedStudentId
                        ? selectedStudentRef
                        : null
                    }
                  >
                    <ListItemText
                      primary={filteredStudents[index].name}
                      style={{ paddingLeft: '10px' }}
                    />
                  </ListItem>
                )}
              </List>
            )}
          </Box>
        </Paper>

        {selectedStudent && (
          <Paper
            elevation={4}
            style={{
              padding: '20px',
              borderRadius: '12px',
              backgroundColor: '#f0f4f8',
              marginTop: '20px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Box
              style={{
                backgroundColor: '#ffffff',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)',
                width: '100%'
              }}
            >
              <Typography
                variant="h5"
                style={{
                  color: '#1976D2',
                  fontWeight: 'bold',
                  marginBottom: '12px'
                }}
              >
                {selectedStudent.name} - {selectedStudent.class}.
                {t('review.grade')}
              </Typography>
              <Typography style={{ marginBottom: '10px', color: '#455a64' }}>
                {t('review.gender')}:
                {selectedStudent.pol === 'm'
                  ? t('review.male')
                  : t('review.female')}
                | {t('review.language')}:
                {selectedStudent.language === 'Х'
                  ? t('review.croatian')
                  : t('review.serbian')}
              </Typography>
              <Typography style={{ marginBottom: '20px', color: '#455a64' }}>
                {t('review.totalPoints')}:
                <strong>{selectedStudent.point_sum}</strong>
              </Typography>
              <Typography
                variant="h6"
                style={{
                  color: '#1976D2',
                  marginBottom: '10px',
                  fontWeight: 'bold'
                }}
              >
                {t('review.school')}: {selectedStudent.school_name}
              </Typography>
              <Typography style={{ color: '#455a64', marginBottom: '10px' }}>
                {t('review.address')}: {selectedStudent.street},
                {selectedStudent.location_name} {selectedStudent.zipcode}
              </Typography>
              <Typography style={{ color: '#455a64', marginBottom: '20px' }}>
                {t('review.professor')}: {selectedStudent.professor}
              </Typography>
              {isAdmin() && (
                <Box
                  display="flex"
                  justifyContent="flex-start"
                  marginTop="20px"
                >
                  <Button
                    variant="contained"
                    style={{
                      backgroundColor: '#1976D2',
                      color: '#ffffff',
                      borderRadius: '8px',
                      padding: '6px 16px',
                      fontSize: '14px',
                      textTransform: 'none'
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = '#1565c0')
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = '#1976D2')
                    }
                    onClick={() => handleDialogToggle(true, selectedStudent)}
                  >
                    {t('review.changeStudentData')}
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>
        )}
        {isAdmin() === false && (
          <Button
            onClick={handleNewComplaint}
            style={{
              backgroundColor: '#d32f2f',
              color: '#ffffff',
              padding: '6px 16px',
              fontSize: '14px',
              textTransform: 'none',
              borderRadius: '12px',
              width: '100%',
              marginTop: '20px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
            }}
          >
            {t('meni.newComplaints')}
          </Button>
        )}
      </Grid>

      <Grid item xs={4}>
        <Paper elevation={4} style={{ padding: '20px' }}>
          {selectedStudentId && (
            <StudentDetails
              studentId={selectedStudentId}
              onUpdate={fetchStudents}
            />
          )}
        </Paper>
      </Grid>

      <Grid item xs={5} style={{ height: '100%' }}>
        <Paper
          elevation={4}
          style={{
            padding: '20px',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}
        >
          {selectedStudent && (
            <img
              src={`${API_BASE_URL}/api/radovi/${selectedStudent.schoolid}/${selectedStudent.image_url} `}
              alt={t('review.studentWork')}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '90%',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                marginBottom: '10px'
              }}
            />
          )}
        </Paper>
      </Grid>

      <CustomModal open={openDialog} onClose={() => setOpenDialog(false)} />

      <AddStudentDialog
        open={openStudentDialog}
        handleClose={() => handleDialogToggle(false)}
        studentData={selectedStudent}
        onSuccess={fetchStudents}
      />

      {/* Dijalog za slučaj da žalba već postoji */}
      <Dialog
        open={openComplaintExistsDialog}
        onClose={() => setOpenComplaintExistsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ position: 'relative' }}>
          <Typography
            variant="h5"
            style={{ fontWeight: 'bold', color: '#1976d2' }}
          >
            {t('complaints.complaintAlreadyExistsTitle')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setOpenComplaintExistsDialog(false)}
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#1976d2'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" alignItems="center">
            <InfoIcon
              style={{ marginRight: 12, fontSize: 40, color: '#1976d2' }}
            />
            <Typography variant="body1" style={{ fontSize: '1.2rem' }}>
              {t('complaints.complaintAlreadyExistsMessage')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '12px' }}>
          <Button
            onClick={() => setOpenComplaintExistsDialog(false)}
            variant="outlined"
            color="primary"
            style={{
              fontWeight: 'bold',
              padding: '10px 24px',
              textTransform: 'none',
              borderRadius: '8px'
            }}
          >
            {t('complaints.close')}
          </Button>
          <Button
            onClick={() => {
              setOpenComplaintExistsDialog(false);
              navigate(`/kengur2/#/complaints/${schoolId}`);
            }}
            variant="contained"
            color="primary"
            style={{
              fontWeight: 'bold',
              padding: '10px 24px',
              textTransform: 'none',
              borderRadius: '8px',
              backgroundColor: '#1976d2'
            }}
          >
            {t('complaints.viewAllComplaints')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SchoolStudents;
